/**
 * DrawToolbar — Mode-switching toolbar for drawing tools.
 * Integrates with mapbox-gl-draw, shows live measurements.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { applyMapLibreDrawShim } from '../mapLibreDrawShim';
import bbox from '@turf/bbox';
import { MapPin, Pentagon, Route, MousePointer2, X, Check, Search, FileText, Landmark } from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useCreateInfrastructure, useCreateParcel, useMapLayers, useInfrastructureTaxonomy, useMapParcel } from '../api';
import type { ArmedInfrastructureTemplate, DrawMode, InfraCategoryType, FeatureType } from '../types';
import { ALL_CATEGORIES, CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS, buildInfraTemplateId, normalizeInfraEntityType } from '../types';
import { useMapperWorkspace } from '../workspaceStore';
import { useQuery } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import area from '@turf/area';
import length from '@turf/length';
import { toast } from 'sonner';
import FinishInfrastructureDialog from './FinishInfrastructureDialog';
import { buildTemplateFinishFields } from '../infrastructureAutoFill';

/* ------------------------------------------------------------------ */
/*  Draw mode config                                                    */
/* ------------------------------------------------------------------ */

const DRAW_TOOLS: Array<{ mode: DrawMode; icon: typeof MapPin; label: string; drawMode: string }> = [
  { mode: 'none', icon: MousePointer2, label: 'Select', drawMode: 'simple_select' },
  { mode: 'point', icon: MapPin, label: 'Marker', drawMode: 'draw_point' },
  { mode: 'polygon', icon: Pentagon, label: 'Polygon', drawMode: 'draw_polygon' },
  { mode: 'line', icon: Route, label: 'Line', drawMode: 'draw_line_string' },
];

const EDITING_INFRA_DRAW_ID = '__editing_infrastructure_geometry__';

function drawModeForGeometry(geometryType?: FeatureType | null): DrawMode {
  if (geometryType === 'Polygon') return 'polygon';
  if (geometryType === 'LineString') return 'line';
  return 'point';
}

type ContextualizerInfraTemplate = {
  id: string;
  name: string;
  entity_type?: string;
  iku_category?: string;
  primary_industry?: string;
  subcategory?: string;
  geometry_type?: FeatureType;
  attributes?: Array<{ key: string; name?: string }>;
};

type QuickObjectPreset = {
  id: string;
  label: string;
  description: string;
  geometry: FeatureType;
  category: InfraCategoryType;
  subcategory: string;
  type: string;
  name: string;
  industryTags?: string[];
  attributes?: Record<string, unknown>;
};

function buildQuickPresetsFromTaxonomy(
  taxonomy: import('../types').InfrastructureTaxonomy | undefined,
  geometryType: FeatureType | null,
  allowedPresetIds?: string[],
): QuickObjectPreset[] {
  if (!taxonomy || !geometryType) return [];
  const seen = new Set<string>();
  const presets: QuickObjectPreset[] = [];

  const pushPreset = (preset: QuickObjectPreset) => {
    if (allowedPresetIds && !allowedPresetIds.includes(preset.id)) return;
    if (seen.has(preset.id)) return;
    seen.add(preset.id);
    presets.push(preset);
  };

  for (const section of taxonomy.checklist?.sections || []) {
    for (const item of section.templates || []) {
      const geom = normalizeGeometryType(item.geometry_type || 'Polygon');
      if (geom !== geometryType) continue;
      const typeKey = String(item.key || '').trim();
      if (!typeKey) continue;
      const templateMeta = taxonomy.templates?.[typeKey];
      pushPreset({
        id: typeKey,
        label: item.label || typeKey.replace(/_/g, ' '),
        description: section.description || item.label || section.label || typeKey,
        geometry: geom,
        category: (item.infrastructure_category || section.key || 'buildings') as InfraCategoryType,
        subcategory: item.subcategory || 'general',
        type: typeKey,
        name: item.label || typeKey.replace(/_/g, ' '),
        industryTags: templateMeta?.industry_tags || [],
      });
    }
  }

  if (presets.length > 0) return presets.slice(0, 8);

  for (const [categoryKey, categoryDef] of Object.entries(taxonomy.categories || {})) {
    const subcategories = categoryDef?.subcategories || {};
    for (const [subKey, typeList] of Object.entries(subcategories)) {
      for (const typeKey of (Array.isArray(typeList) ? typeList : [])) {
        const templateMeta = taxonomy.templates?.[typeKey];
        const geom = normalizeGeometryType(templateMeta?.geometry_type || (categoryKey === 'linear' ? 'LineString' : categoryKey === 'waste_environmental' ? 'Point' : 'Polygon'));
        if (geom !== geometryType) continue;
        pushPreset({
          id: typeKey,
          label: templateMeta?.label || typeKey.replace(/_/g, ' '),
          description: categoryDef?.description || templateMeta?.label || typeKey,
          geometry: geom,
          category: categoryKey as InfraCategoryType,
          subcategory: subKey,
          type: typeKey,
          name: templateMeta?.label || typeKey.replace(/_/g, ' '),
          industryTags: templateMeta?.industry_tags || [],
        });
      }
    }
  }

  return presets.slice(0, 8);
}

function normalizeGeometryType(type: string | undefined): FeatureType | null {
  switch (type) {
    case 'Polygon':
    case 'MultiPolygon':
      return 'Polygon';
    case 'LineString':
    case 'MultiLineString':
      return 'LineString';
    case 'Point':
    case 'MultiPoint':
      return 'Point';
    default:
      return null;
  }
}

function templateMatchesGeometry(template: ContextualizerInfraTemplate, geometry: GeoJSON.Geometry): boolean {
  const templateType = normalizeGeometryType(template.geometry_type);
  const drawnType = normalizeGeometryType(geometry.type);
  return !templateType || !drawnType || templateType === drawnType;
}

function isVisuallyEditableGeometry(geometry: GeoJSON.Geometry | null | undefined): geometry is GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon {
  return !!geometry && (
    geometry.type === 'Point' ||
    geometry.type === 'LineString' ||
    geometry.type === 'Polygon'
  );
}

function inferCategoryFromTemplate(template: ContextualizerInfraTemplate, fallback: InfraCategoryType): InfraCategoryType {
  const raw = [
    template.entity_type,
    template.name,
    template.iku_category,
    template.subcategory,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (raw.includes('stream') || raw.includes('river') || raw.includes('wetland') || raw.includes('forest')) {
    return 'natural_environment';
  }
  if (raw.includes('field') || raw.includes('orchard') || raw.includes('crop') || raw.includes('pasture') || raw.includes('land')) {
    return 'land_use';
  }
  if (raw.includes('pipeline') || raw.includes('pipe') || raw.includes('road') || raw.includes('track') || raw.includes('fence') || raw.includes('line')) {
    return 'linear';
  }
  if (raw.includes('dam') || raw.includes('reservoir') || raw.includes('irrigation') || raw.includes('water')) {
    return 'water_infrastructure';
  }
  if (raw.includes('solar') || raw.includes('wind') || raw.includes('power') || raw.includes('energy') || raw.includes('electric')) {
    return 'energy_utilities';
  }
  if (raw.includes('waste') || raw.includes('effluent') || raw.includes('manure') || raw.includes('lagoon')) {
    return 'waste_environmental';
  }
  if (raw.includes('building') || raw.includes('shed') || raw.includes('silo') || raw.includes('barn') || raw.includes('facility')) {
    return 'buildings';
  }
  return fallback;
}

/* ------------------------------------------------------------------ */
/*  Live Measurement                                                    */
/* ------------------------------------------------------------------ */

function MeasurementLabel({ geojson }: { geojson: GeoJSON.Feature | null }) {
  if (!geojson || !geojson.geometry) return null;

  let label = '';
  try {
    const geomType = geojson.geometry.type;
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      const areaM2 = area(geojson);
      const areaHa = areaM2 / 10000;
      label = areaHa >= 1
        ? `${areaHa.toFixed(2)} ha`
        : `${areaM2.toFixed(0)} m²`;
    } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
      const len = length(geojson, { units: 'meters' });
      label = len >= 1000
        ? `${(len / 1000).toFixed(2)} km`
        : `${len.toFixed(0)} m`;
    }
  } catch {
    return null;
  }

  if (!label) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-gray-900/90 text-white text-sm font-mono px-4 py-2 rounded-full shadow-lg backdrop-blur-sm z-30"
    >
      {label}
    </motion.div>
  );
}

function CreateParcelDialog({
  onConfirm,
  onCancel,
  isSubmitting,
}: {
  onConfirm: (name: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}) {
  const [name, setName] = useState('');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.96, y: 12, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 12, opacity: 0 }}
        className="w-full max-w-md rounded-2xl border border-emerald-200 bg-white p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <Landmark className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-slate-900">Create farm boundary</h3>
            <p className="mt-1 text-sm text-slate-600">
              Name this farm now. You can link a legal entity and add more detail later.
            </p>
          </div>
        </div>

        <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
          Farm name
        </label>
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim() && !isSubmitting) {
              onConfirm(name.trim());
            }
          }}
          placeholder="e.g. Appleby Farm"
          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(name.trim())}
            disabled={!name.trim() || isSubmitting}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create farm'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Infrastructure Classification Dialog                                */
/* ------------------------------------------------------------------ */

function useContextualizerInfraTemplates() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['templates', 'iku-infrastructure', 'draw-toolbar', appId],
    queryFn: async () => {
      const res = await cpFetch('/api/templates?ikuCategory=iku-infrastructure&limit=250');
      return (res?.templates ?? res?.items ?? []) as ContextualizerInfraTemplate[];
    },
    enabled: !!token && !!appId,
    staleTime: 60_000,
  });
}

function InfraClassifyDialog({
  geometry,
  onConfirm,
  onCancel,
  defaultCategory,
  activeTemplateIds,
  allowedPresetIds,
  isSubmitting,
}: {
  geometry: GeoJSON.Geometry;
  onConfirm: (data: {
    name: string;
    category: InfraCategoryType;
    subcategory: string;
    type: string;
    industry_tags: string[];
    template_id?: string;
    attributes?: Record<string, unknown>;
  }) => void;
  onCancel: () => void;
  defaultCategory: InfraCategoryType | null;
  activeTemplateIds: string[];
  allowedPresetIds?: string[];
  isSubmitting?: boolean;
}) {
  const { data: taxonomy } = useInfrastructureTaxonomy();
  const { data: ctxTemplates } = useContextualizerInfraTemplates();
  const [mode, setMode] = useState<'taxonomy' | 'template'>(() => (activeTemplateIds.length ? 'template' : 'taxonomy'));
  const [category, setCategory] = useState<InfraCategoryType>(defaultCategory || 'buildings');
  const [subcategory, setSubcategory] = useState('');
  const [infraType, setInfraType] = useState('');
  const [name, setName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const geometryType = normalizeGeometryType(geometry.type);
  const quickPresets = buildQuickPresetsFromTaxonomy(taxonomy, geometryType, allowedPresetIds);

  const catDef = taxonomy?.categories?.[category];
  const subcategories = catDef?.subcategories || {};
  const types = subcategory ? (subcategories[subcategory] || []) : [];

  useEffect(() => {
    if (mode !== 'taxonomy') return;
    const subs = Object.keys(subcategories);
    if (subs.length && !subs.includes(subcategory)) {
      setSubcategory(subs[0]);
      const ts = subcategories[subs[0]] || [];
      setInfraType(ts[0] || '');
    }
  }, [category, taxonomy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== 'taxonomy') return;
    const ts = subcategories[subcategory] || [];
    if (ts.length && !ts.includes(infraType)) {
      setInfraType(ts[0]);
    }
  }, [subcategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== 'taxonomy') return;
    if (infraType && !name) {
      setName(infraType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    }
  }, [infraType]); // eslint-disable-line react-hooks/exhaustive-deps

  const tmpl = taxonomy?.templates?.[infraType];
  const industryTags = tmpl?.industry_tags || [];
  const activeTemplateIdSet = new Set(activeTemplateIds);

  const filteredTemplates = (ctxTemplates ?? [])
    .filter((t) => templateMatchesGeometry(t, geometry))
    .filter((t) => !templateSearch || t.name.toLowerCase().includes(templateSearch.toLowerCase()))
    .sort((a, b) => {
      const aRecommended = activeTemplateIdSet.has(a.id) ? 1 : 0;
      const bRecommended = activeTemplateIdSet.has(b.id) ? 1 : 0;
      if (aRecommended !== bRecommended) return bRecommended - aRecommended;
      return a.name.localeCompare(b.name);
    });

  const selectedTemplate = ctxTemplates?.find(t => t.id === selectedTemplateId);
  const selectedPreset = quickPresets.find((preset) => preset.id === selectedPresetId) || null;

  useEffect(() => {
    if (quickPresets.length === 0) {
      setSelectedPresetId(null);
      setShowAdvanced(true);
      return;
    }
    setSelectedPresetId((current) => {
      if (current && quickPresets.some((preset) => preset.id === current)) return current;
      return quickPresets[0].id;
    });
  }, [quickPresets]);

  useEffect(() => {
    if (!selectedPreset) return;
    setCategory(selectedPreset.category);
    setSubcategory(selectedPreset.subcategory);
    setInfraType(selectedPreset.type);
    if (!name || quickPresets.some((preset) => preset.name === name)) {
      setName(selectedPreset.name);
    }
  }, [selectedPreset, name]);

  useEffect(() => {
    if (activeTemplateIds.length > 0 && mode === 'taxonomy' && !selectedTemplateId) {
      setMode('template');
    }
  }, [activeTemplateIds, mode, selectedTemplateId]);

  useEffect(() => {
    if (mode !== 'template') return;
    if (selectedTemplateId && filteredTemplates.some((t) => t.id === selectedTemplateId)) return;
    if (filteredTemplates.length === 0) {
      if (selectedTemplateId) setSelectedTemplateId(null);
      return;
    }
    const recommended = filteredTemplates.find((t) => activeTemplateIdSet.has(t.id));
    const next = recommended?.id || filteredTemplates[0].id;
    setSelectedTemplateId(next);
    if (!name) {
      setName((recommended || filteredTemplates[0]).name);
    }
  }, [mode, filteredTemplates, selectedTemplateId, name, activeTemplateIdSet]);

  const handleConfirm = () => {
    if (!showAdvanced && selectedPreset) {
      onConfirm({
        name: name.trim() || selectedPreset.name,
        category: selectedPreset.category,
        subcategory: selectedPreset.subcategory,
        type: selectedPreset.type,
        industry_tags: selectedPreset.industryTags || [],
        template_id: buildInfraTemplateId(selectedPreset.category, selectedPreset.subcategory, selectedPreset.type),
        attributes: selectedPreset.attributes,
      });
    } else if (mode === 'template' && selectedTemplate) {
      const fallbackCategory = defaultCategory || 'buildings';
      onConfirm({
        name: name.trim() || selectedTemplate.name,
        category: inferCategoryFromTemplate(selectedTemplate, fallbackCategory),
        subcategory: selectedTemplate.subcategory || '',
        type: selectedTemplate.entity_type || selectedTemplate.name,
        industry_tags: selectedTemplate.iku_category ? [selectedTemplate.iku_category] : [],
        template_id: selectedTemplate.id,
      });
    } else {
      onConfirm({
        name: name.trim() || infraType,
        category,
        subcategory,
        type: infraType,
        industry_tags: industryTags,
        template_id: buildInfraTemplateId(category, subcategory, infraType),
      });
    }
  };

  const canConfirm = (!!selectedPresetId || (mode === 'template' ? !!selectedTemplateId : !!infraType)) && !isSubmitting;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500">
        <h4 className="text-sm font-semibold text-white">Add Object Layer</h4>
        <p className="text-[10px] text-white/80">Choose a quick type first. Open advanced options only when you need more control.</p>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-gray-400">Quick presets</div>
          {quickPresets.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-500">
              No quick preset fits this geometry yet. Use advanced options below.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {quickPresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    setSelectedPresetId(preset.id);
                    setShowAdvanced(false);
                  }}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    selectedPresetId === preset.id && !showAdvanced
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200 bg-white hover:border-amber-200 hover:bg-amber-50/60'
                  }`}
                >
                  <div className="text-xs font-semibold text-gray-900">{preset.label}</div>
                  <div className="mt-1 text-[10px] text-gray-500">{preset.description}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] text-gray-500 font-medium block mb-1">Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder={selectedPreset?.name || 'e.g. Block A, Main Dam...'}
            className="w-full text-xs px-3 py-2 rounded-md border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none" />
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced((current) => !current)}
          className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
        >
          {showAdvanced ? 'Hide advanced options' : 'Open advanced options'}
        </button>

        {showAdvanced && (
          <>
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setMode('taxonomy')}
                className={`flex-1 text-xs font-medium py-2 transition-colors ${
                  mode === 'taxonomy' ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                Taxonomy
              </button>
              <button
                onClick={() => setMode('template')}
                className={`flex-1 text-xs font-medium py-2 transition-colors flex items-center justify-center gap-1 ${
                  mode === 'template' ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <FileText className="w-3 h-3" /> From Template
              </button>
            </div>

            {mode === 'taxonomy' ? (
              <>
                <div className="flex gap-1 flex-wrap">
                  {ALL_CATEGORIES.map(cat => {
                    const IC = CATEGORY_ICONS[cat];
                    const clr = CATEGORY_COLORS[cat];
                    return (
                      <button key={cat} onClick={() => setCategory(cat)}
                        className={`text-[10px] px-2 py-1 rounded-full font-medium flex items-center gap-1 transition-all ${
                          category === cat ? `${clr.bg} ${clr.text}` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}>
                        <IC className="w-3 h-3" />
                        {CATEGORY_LABELS[cat]?.split(' ')[0] || cat}
                      </button>
                    );
                  })}
                </div>

                <div>
                  <label className="text-[10px] text-gray-500 font-medium block mb-1">Subcategory</label>
                  <select value={subcategory} onChange={e => setSubcategory(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-md border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none">
                    {Object.keys(subcategories).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] text-gray-500 font-medium block mb-1">Type</label>
                  <select value={infraType} onChange={e => setInfraType(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-md border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none">
                    {types.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>

                {industryTags.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {industryTags.map(tag => (
                      <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{tag}</span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {activeTemplateIds.length > 0 && (
                  <div className="rounded-md border border-teal-200 bg-teal-50 px-2.5 py-2">
                    <p className="text-[10px] font-medium text-teal-700">Parcel Contextualizer templates</p>
                    <p className="text-[10px] text-teal-600/80">Parcel-linked templates are shown first so buildings, utilities, and other mapped infrastructure can be created faster.</p>
                  </div>
                )}
                <div className="flex items-center gap-1.5 bg-gray-50 rounded-md px-2 py-1.5">
                  <Search className="w-3 h-3 text-gray-400" />
                  <input
                    autoFocus
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search templates..."
                    className="text-xs bg-transparent outline-none flex-1"
                  />
                </div>
                <div className="max-h-36 overflow-y-auto space-y-0.5">
                  {filteredTemplates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setSelectedTemplateId(t.id);
                        if (!name) setName(t.name);
                      }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                        selectedTemplateId === t.id
                          ? 'bg-amber-50 border border-amber-200'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-gray-700">{t.name}</span>
                      {activeTemplateIdSet.has(t.id) && (
                        <span className="text-[9px] text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                          parcel
                        </span>
                      )}
                      {t.attributes && (
                        <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">
                          {t.attributes.length} attrs
                        </span>
                      )}
                    </button>
                  ))}
                  {filteredTemplates.length === 0 && (
                    <p className="text-xs text-gray-300 italic text-center py-3">No templates found</p>
                  )}
                </div>
                {selectedTemplate && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
                    <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Template context</div>
                    <div className="mt-1 text-xs text-gray-700">{selectedTemplate.entity_type || selectedTemplate.name}</div>
                    <div className="mt-0.5 text-[10px] text-gray-400">
                      {selectedTemplate.attributes?.length || 0} attributes will be seeded from Contextualizer.
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="px-4 pb-4 flex gap-2">
        <motion.button whileTap={{ scale: 0.95 }} onClick={handleConfirm}
          disabled={!canConfirm}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 disabled:opacity-40 transition-colors">
          <Check className="w-3.5 h-3.5" /> {isSubmitting ? 'Creating...' : 'Create'}
        </motion.button>
        <motion.button whileTap={{ scale: 0.95 }} onClick={onCancel}
          className="py-2 px-4 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100 transition-colors">
          Cancel
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export default function DrawToolbar({
  quickPresetIds,
  placement = 'floating',
}: {
  quickPresetIds?: string[];
  placement?: 'floating' | 'inline';
} = {}) {
  const drawRef = useRef<MapboxDraw | null>(null);
  const {
    map,
    drawMode,
    setDrawMode,
    parcelCreationMode,
    setParcelCreationMode,
    pendingParcelGeometry,
    setPendingParcelGeometry,
    activeDrawLayerId,
    selectedLayer,
    selectedInfrastructure,
    activeParcel: activeParcelFromStore,
    activeFarmGroup,
    activeCategory,
    pendingDrawGeometry,
    setPendingDrawGeometry,
    armedTemplate,
    setArmedTemplate,
    setSelectedInfrastructure,
    setActiveParcel,
    setParcelInspectorOpen,
    setShowBoundaries,
    triggerFlyTo,
    editingInfrastructureGeometryId,
    editingInfrastructureGeometrySeed,
    setEditingInfrastructureGeometryDraft,
    clearEditingInfrastructureGeometry,
  } = useMapStore();
  const { saveFarm, setActiveFarm } = useMapperWorkspace();
  const { data: activeParcelDetail } = useMapParcel(activeParcelFromStore?.id);
  const activeParcel = activeParcelDetail || activeParcelFromStore;
  const createInfra = useCreateInfrastructure();
  const createParcel = useCreateParcel();
  const { data: layerData } = useMapLayers({ enabled: true });
  const layers = layerData?.layers ?? [];

  const [liveMeasurement, setLiveMeasurement] = useState<GeoJSON.Feature | null>(null);
  const [pendingTemplateDraft, setPendingTemplateDraft] = useState<{
    geometry: GeoJSON.Geometry;
    template: ArmedInfrastructureTemplate;
  } | null>(null);
  const geometryEditingActive = !!editingInfrastructureGeometryId;
  const isDrawing = drawMode !== 'none' || geometryEditingActive;

  // Target layer: active draw layer, selected layer, or first layer
  const targetLayerId = activeDrawLayerId || selectedLayer?.id || layers[0]?.id;
  const templateFinishFields = pendingTemplateDraft
    ? buildTemplateFinishFields(pendingTemplateDraft.template, pendingTemplateDraft.geometry, {
      land_category: activeParcel?.land_category || null,
      linked_entity_id: activeParcel?.linked_entity_id || activeFarmGroup?.linked_entity_id || null,
    })
    : [];

  /* ---------------------------------------------------------------- */
  /*  Init / cleanup mapbox-gl-draw                                     */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!map) return;

    applyMapLibreDrawShim();
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: 'simple_select',
      styles: [
        // Polygon fill
        { id: 'gl-draw-polygon-fill', type: 'fill', filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: { 'fill-color': '#3b82f6', 'fill-outline-color': '#3b82f6', 'fill-opacity': 0.15 } },
        // Polygon outline
        { id: 'gl-draw-polygon-stroke-active', type: 'line', filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: { 'line-color': '#3b82f6', 'line-width': 2.5 } },
        // Line
        { id: 'gl-draw-line', type: 'line', filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          paint: { 'line-color': '#3b82f6', 'line-width': 2.5, 'line-dasharray': [2, 2] } },
        // Vertex point
        { id: 'gl-draw-point', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
          paint: { 'circle-radius': 6, 'circle-color': '#ffffff', 'circle-stroke-color': '#3b82f6', 'circle-stroke-width': 2 } },
        // Midpoint
        { id: 'gl-draw-midpoint', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
          paint: { 'circle-radius': 4, 'circle-color': '#3b82f6', 'circle-opacity': 0.5 } },
        // Standalone point
        { id: 'gl-draw-point-standalone', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'vertex'], ['!=', 'meta', 'midpoint']],
          paint: { 'circle-radius': 8, 'circle-color': '#3b82f6', 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 } },
      ],
    });

    map.addControl(draw as any);
    drawRef.current = draw;

    // Listen for draw.create — show classification dialog instead of creating a plain feature
    const handleCreate = (e: any) => {
      if (useMapStore.getState().editingInfrastructureGeometryId) return;
      const features = e.features;
      if (!features?.length) return;
      const feat = features[0];

      if (useMapStore.getState().parcelCreationMode) {
        setPendingParcelGeometry(feat.geometry);
        draw.deleteAll();
        setDrawMode('none');
        setLiveMeasurement(null);
        return;
      }

      const currentArmedTemplate = useMapStore.getState().armedTemplate;
      if (currentArmedTemplate) {
        setPendingTemplateDraft({
          geometry: feat.geometry,
          template: currentArmedTemplate,
        });
        draw.deleteAll();
        setDrawMode('none');
        setLiveMeasurement(null);
        return;
      }

      // Store geometry and show classify dialog
      setPendingDrawGeometry(feat.geometry);

      // Clear drawn features from draw control
      draw.deleteAll();
      setDrawMode('none');
      setLiveMeasurement(null);
    };

    // Live measurement during drawing
    const handleRender = () => {
      const all = draw.getAll();
      const {
        editingInfrastructureGeometryId: activeEditId,
        setEditingInfrastructureGeometryDraft: setGeometryDraft,
      } = useMapStore.getState();

      if (activeEditId) {
        const editingFeature = all.features.find((feature) => feature.id === EDITING_INFRA_DRAW_ID) || all.features[0];
        if (editingFeature?.geometry) {
          setGeometryDraft(editingFeature.geometry as GeoJSON.Geometry);
          setLiveMeasurement(editingFeature as GeoJSON.Feature);
        } else {
          setGeometryDraft(null);
          setLiveMeasurement(null);
        }
        return;
      }

      if (all.features.length > 0) {
        const last = all.features[all.features.length - 1];
        setLiveMeasurement(last as GeoJSON.Feature);
      } else {
        setLiveMeasurement(null);
      }
    };

    map.on('draw.create', handleCreate);
    map.on('draw.render', handleRender);
    map.on('draw.update', handleRender);
    map.on('draw.delete', handleRender);

    return () => {
      map.off('draw.create', handleCreate);
      map.off('draw.render', handleRender);
      map.off('draw.update', handleRender);
      map.off('draw.delete', handleRender);
      if (drawRef.current) {
        try { map.removeControl(drawRef.current as any); } catch { /* ignore */ }
      }
      drawRef.current = null;
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------------------------------------------------------- */
  /*  Switch draw mode                                                  */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!drawRef.current) return;
    if (geometryEditingActive) return;
    const tool = DRAW_TOOLS.find((t) => t.mode === drawMode);
    if (tool) {
      drawRef.current.changeMode(tool.drawMode as any);
    }
  }, [drawMode, geometryEditingActive]);

  /* ---------------------------------------------------------------- */
  /*  Load existing infrastructure geometry into draw                    */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (!drawRef.current) return;

    const draw = drawRef.current;
    const clearEditingFeature = () => {
      try {
        if (draw.get(EDITING_INFRA_DRAW_ID)) {
          draw.delete(EDITING_INFRA_DRAW_ID);
        }
      } catch {
        /* ignore */
      }
    };

    if (!editingInfrastructureGeometryId || !editingInfrastructureGeometrySeed) {
      clearEditingFeature();
      if (drawMode === 'none') {
        setLiveMeasurement(null);
      }
      return;
    }

    if (!isVisuallyEditableGeometry(editingInfrastructureGeometrySeed)) {
      clearEditingInfrastructureGeometry();
      toast.error('Visual geometry editing currently supports points, lines, and polygons only.');
      return;
    }

    draw.deleteAll();
    const editingFeature: GeoJSON.Feature = {
      type: 'Feature',
      id: EDITING_INFRA_DRAW_ID,
      properties: {
        _editing_infrastructure_id: editingInfrastructureGeometryId,
      },
      geometry: editingInfrastructureGeometrySeed,
    };

    draw.add(editingFeature as any);
    setEditingInfrastructureGeometryDraft(editingInfrastructureGeometrySeed);
    setLiveMeasurement(editingFeature);

    const rafId = window.requestAnimationFrame(() => {
      try {
        if (editingInfrastructureGeometrySeed.type === 'Point') {
          draw.changeMode('simple_select', { featureIds: [EDITING_INFRA_DRAW_ID] } as any);
        } else {
          draw.changeMode('direct_select', { featureId: EDITING_INFRA_DRAW_ID } as any);
        }
      } catch {
        draw.changeMode('simple_select', { featureIds: [EDITING_INFRA_DRAW_ID] } as any);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    drawMode,
    editingInfrastructureGeometryId,
    editingInfrastructureGeometrySeed,
    setEditingInfrastructureGeometryDraft,
    clearEditingInfrastructureGeometry,
  ]);

  /* ---------------------------------------------------------------- */
  /*  Keyboard shortcuts                                                */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (geometryEditingActive) {
          clearEditingInfrastructureGeometry();
          drawRef.current?.deleteAll();
          setLiveMeasurement(null);
          return;
        }
        setParcelCreationMode(false);
        setPendingParcelGeometry(null);
        setPendingTemplateDraft(null);
        setArmedTemplate(null);
        setDrawMode('none');
        drawRef.current?.deleteAll();
        setLiveMeasurement(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    setDrawMode,
    geometryEditingActive,
    clearEditingInfrastructureGeometry,
    setParcelCreationMode,
    setPendingParcelGeometry,
    setArmedTemplate,
  ]);

  const toolbarTools = DRAW_TOOLS;

  /* ---------------------------------------------------------------- */
  /*  Render                                                            */
  /* ---------------------------------------------------------------- */
  return (
    <>
      <motion.div
        layout
        className={`
          flex items-center gap-1 p-1 rounded-xl shadow-lg backdrop-blur-md border
          transition-colors duration-300
          ${placement === 'floating' ? 'absolute top-4 left-1/2 -translate-x-1/2 z-30' : 'w-max max-w-full shadow-sm'}
          ${isDrawing ? 'bg-blue-50/95 border-blue-200' : 'bg-white/95 border-gray-200'}
        `}
      >
        {toolbarTools.map((tool) => {
          const Icon = tool.icon;
          const label = tool.label;
          const active = !geometryEditingActive && drawMode === tool.mode;
          return (
            <motion.button
              key={tool.mode}
              whileTap={{ scale: 0.92 }}
              onClick={() => {
                if (geometryEditingActive) return;
                if (tool.mode === 'polygon' && !activeParcel) {
                  setPendingDrawGeometry(null);
                  setPendingParcelGeometry(null);
                  setPendingTemplateDraft(null);
                  setArmedTemplate(null);
                  setParcelCreationMode(true);
                  setDrawMode('polygon');
                  toast.info('Draw a land unit boundary, then double-click to finish.', { duration: 3000 });
                  return;
                }
                setParcelCreationMode(false);
                setPendingTemplateDraft(null);
                setArmedTemplate(null);
                setDrawMode(tool.mode, targetLayerId);
              }}
              disabled={geometryEditingActive}
              title={geometryEditingActive ? 'Finish geometry editing first' : undefined}
              className={`
                relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium
                transition-all duration-200
                ${active
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
                ${geometryEditingActive ? 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-gray-600' : ''}
              `}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
              {active && (
                <motion.div
                  layoutId="drawActiveIndicator"
                  className="absolute inset-0 bg-blue-600 rounded-lg -z-10"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </motion.button>
          );
        })}

        {/* Cancel button when drawing */}
        <AnimatePresence>
          {isDrawing && (
            <motion.button
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (geometryEditingActive) {
                  clearEditingInfrastructureGeometry();
                  drawRef.current?.deleteAll();
                  setLiveMeasurement(null);
                  return;
                }
                setParcelCreationMode(false);
                setPendingParcelGeometry(null);
                setDrawMode('none');
                drawRef.current?.deleteAll();
                setLiveMeasurement(null);
              }}
              className="flex items-center gap-1 px-2 py-2 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors overflow-hidden"
            >
              <X className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Cancel</span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Layer indicator */}
        {geometryEditingActive ? (
          <div className="hidden sm:flex items-center gap-1 px-2 text-[10px] text-blue-700 border-l border-blue-200 ml-1 pl-2">
            Editing: <span className="font-medium text-blue-800 truncate max-w-[120px]">
              {selectedInfrastructure?.name || 'Infrastructure geometry'}
            </span>
          </div>
        ) : parcelCreationMode ? (
          <div className="hidden sm:flex items-center gap-1 px-2 text-[10px] text-emerald-700 border-l border-emerald-200 ml-1 pl-2">
            Drawing: <span className="font-medium text-emerald-800 truncate max-w-[120px]">New land unit boundary</span>
          </div>
        ) : targetLayerId && (
          <div className="hidden sm:flex items-center gap-1 px-2 text-[10px] text-gray-400 border-l border-gray-200 ml-1 pl-2">
            Drawing to: <span className="font-medium text-gray-600 truncate max-w-[80px]">
              {layers.find(l => l.id === targetLayerId)?.name || 'Layer'}
            </span>
          </div>
        )}
      </motion.div>

      {/* Live measurement */}
      <AnimatePresence>
        {isDrawing && liveMeasurement && (
          <MeasurementLabel geojson={liveMeasurement} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {parcelCreationMode && drawMode === 'polygon' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-20 left-1/2 z-30 -translate-x-1/2 rounded-full bg-emerald-900/85 px-4 py-2 text-xs font-medium text-emerald-100 shadow-lg backdrop-blur-sm"
          >
            Draw around the farm boundary, then double-click to finish.
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {armedTemplate && !pendingTemplateDraft && !pendingDrawGeometry && !parcelCreationMode && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-20 left-1/2 z-30 flex -translate-x-1/2 items-center gap-3 rounded-full bg-sky-900/90 px-4 py-2 text-xs font-medium text-sky-50 shadow-lg backdrop-blur-sm"
          >
            <span>Draw the {armedTemplate.name} on the map, then double-click to finish.</span>
            <button
              type="button"
              onClick={() => {
                setArmedTemplate(null);
                setDrawMode('none');
                drawRef.current?.deleteAll();
                setLiveMeasurement(null);
              }}
              className="rounded-full bg-white/10 p-1 transition-colors hover:bg-white/20"
              title="Cancel template drawing"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pendingTemplateDraft && (
          <FinishInfrastructureDialog
            templateName={pendingTemplateDraft.template.name}
            initialName={pendingTemplateDraft.template.name}
            fields={templateFinishFields}
            isSubmitting={createInfra.isPending}
            onConfirm={({ name, attributes }) => {
              createInfra.mutate({
                name,
                infra_type: normalizeInfraEntityType(
                  pendingTemplateDraft.template.category,
                  pendingTemplateDraft.template.subcategory,
                  pendingTemplateDraft.template.type,
                ),
                category: pendingTemplateDraft.template.category,
                subcategory: pendingTemplateDraft.template.subcategory,
                land_category: activeParcel?.land_category || undefined,
                industry_tags: pendingTemplateDraft.template.industry_tags,
                template_id: pendingTemplateDraft.template.template_id,
                geometry: pendingTemplateDraft.geometry,
                parcel_id: activeParcel?.id || activeFarmGroup?.parcel_ids?.[0],
                layer_id: targetLayerId,
                attributes,
                linked_entity_id: activeParcel?.linked_entity_id || activeFarmGroup?.linked_entity_id || undefined,
              }, {
                onSuccess: (created) => {
                  setPendingTemplateDraft(null);
                  setSelectedInfrastructure(created);
                  setDrawMode(drawModeForGeometry(pendingTemplateDraft.template.geometry_type), targetLayerId);
                  toast.success(`${created.name} added. Draw another ${pendingTemplateDraft.template.name.toLowerCase()} or cancel the template banner.`);
                },
                onError: (err) => {
                  toast.error('Infrastructure creation failed', {
                    description: String(err),
                  });
                },
              });
            }}
            onCancel={() => {
              const template = pendingTemplateDraft.template;
              setPendingTemplateDraft(null);
              setDrawMode(drawModeForGeometry(template.geometry_type), targetLayerId);
            }}
          />
        )}
      </AnimatePresence>

      {/* Infrastructure classification dialog */}
      <AnimatePresence>
        {pendingDrawGeometry && (
          <InfraClassifyDialog
            geometry={pendingDrawGeometry}
            defaultCategory={activeCategory}
            activeTemplateIds={activeParcel?.active_template_ids ?? []}
            allowedPresetIds={quickPresetIds}
            isSubmitting={createInfra.isPending}
            onConfirm={({ name, category, subcategory, type, industry_tags, template_id, attributes }) => {
              createInfra.mutate({
                name,
                infra_type: normalizeInfraEntityType(category, subcategory, type),
                category,
                subcategory,
                industry_tags,
                template_id,
                geometry: pendingDrawGeometry,
                parcel_id: activeParcel?.id,
                layer_id: targetLayerId,
                attributes,
                linked_entity_id: activeParcel?.linked_entity_id || undefined,
              }, {
                onSuccess: (created) => {
                  setPendingDrawGeometry(null);
                  setSelectedInfrastructure(created);
                  toast.success(`${created.name} added to the map`);
                },
                onError: (err) => {
                  toast.error('Infrastructure creation failed', {
                    description: String(err),
                  });
                },
              });
            }}
            onCancel={() => setPendingDrawGeometry(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {pendingParcelGeometry && (
          <CreateParcelDialog
            isSubmitting={createParcel.isPending}
            onConfirm={(name) => {
              createParcel.mutate({
                feature_name: name,
                farm_name: name,
                geometry: pendingParcelGeometry,
                land_category: 'agricultural',
                land_type: 'farm_portion',
                country_code: 'ZA',
              }, {
                onSuccess: (parcel) => {
                  setPendingParcelGeometry(null);
                  setParcelCreationMode(false);
                  setActiveParcel(parcel);
                  setShowBoundaries(true);
                  setParcelInspectorOpen(true);
                  saveFarm(parcel);
                  setActiveFarm(parcel.id, parcel.linked_entity_id || null);
                  try {
                    const bounds = bbox({ type: 'Feature', geometry: parcel.geometry, properties: {} });
                    triggerFlyTo({
                      center: [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2],
                      bounds: bounds as [number, number, number, number],
                    });
                  } catch {
                    // Ignore malformed geometry when focusing the new parcel.
                  }
                  toast.success(`${parcel.farm_name || parcel.feature_name || 'Farm'} created`);
                },
                onError: (error) => {
                  toast.error('Could not create the farm boundary', {
                    description: String(error),
                  });
                },
              });
            }}
            onCancel={() => {
              setPendingParcelGeometry(null);
              setParcelCreationMode(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
